import WebRTCIssueDetector from "webrtc-issue-detector";
import { getLogger } from "@/lib/logger";

const logger = getLogger("webrtc");
// Handed to webrtc-issue-detector so its own output is attributable to it rather
// than printing bare.
const detectorLogger = getLogger("webrtc:detector");

class WebRTCConnectionIssueDetector {
  detector;
  constructor(issueListener, networkScoresListener, statsIntervalMs) {
    logger.debug('stats interval configured', { interval_ms: statsIntervalMs });
    const params = {
      onIssues: issueListener,
      onNetworkScoresUpdated: networkScoresListener,
      logger: detectorLogger,
      autoAddPeerConnections: false,
      getStatsInterval: statsIntervalMs || 10000,
    }
    this.detector = new WebRTCIssueDetector(params);
  }

  setPeerConnection(pc) {
    try {
      this.detector.handleNewPeerConnection(pc);
      logger.debug('peer connection set for stats reporting');
    } catch (err) {
      logger.warn('failed to set peer connection for issue detector', err);
    }
  }

  stop() {
    try {
      if (this.detector) {
        this.detector.stopWatchingNewPeerConnections();
      }
    } catch (err) {
      logger.warn('failed to stop stats reporter', err);
    }
  }
}

class WebRTCConnectionWatcher {
  #eventListeners = new Map()
  #connection
  // need different detectors since they can collect stats at different intervals
  #qualityDetector
  #issueDetector
  #webrtcIssuesQueue = { stats: [], warning: [] }
  #webrtcIssuesIntervalTime = 5 * 60 * 1000 // 5 minutes
  #webrtcFlushIssuesInterval = null
  constructor({ connection, qualityIntervalMs, statsIntervalMs }) {
    this.#connection = connection
    if (!this.#connection) {
      throw new Error("connection is required")
    }
    if (qualityIntervalMs > 0) {
      this.#qualityDetector = new WebRTCConnectionIssueDetector(
        (issues) => {
          this.emit("quality-updated", { issues })
        },
        (scores) => {
          this.overwriteInboundScore(scores)
          this.emit("quality-updated", { scores })
        },
        qualityIntervalMs
      )
    } else {
      logger.debug('quality detector not enabled');
    }
    if (statsIntervalMs > 0) {
      this.#issueDetector = new WebRTCConnectionIssueDetector(
        (issues) => {
          this.#webrtcIssuesQueue.warning.push({ timestamp: Date.now(), metrics: issues })
        },
        (scores) => {
          // https://www.webrtc-developers.com/webrtc-statistics-using-getstats/#display-a-quality-indicator
          /* example payload:
          {
            inbound: 4.403401591252691,
            statsSamples: {
              inboundStatsSample: {
                avgJitter: 0.002,
                packetLoss: 0,
                rtt: 2
              }
            }
          }
          */
          // only tracking inbound
          if (scores.inbound) {
            this.overwriteInboundScore(scores)
            this.#webrtcIssuesQueue.stats.push({ timestamp: Date.now(), metrics: scores })
          }
        },
        statsIntervalMs
      )
    } else {
      logger.debug('issue detector not enabled');
    }
  }

  // Override the inbound score with the calculated MOS because we don't trust the detector.
  overwriteInboundScore(scores) {
    if (scores.inbound) {
      const detector_mos = scores.inbound
      const calculated_mos = this.calculateMOS(scores.statsSamples.inboundStatsSample)

      if (detector_mos.toFixed(2) !== calculated_mos.toFixed(2)) {
        logger.debug('mos scores differ', { detector: detector_mos.toFixed(2), calculated: calculated_mos.toFixed(2) });
      }

      scores.inbound = calculated_mos
      scores.inbound_detector = detector_mos
    }
  }

  calculateMOS({ rtt, avgJitter, packetsLoss }) {
    const effectiveLatency = rtt + 2 * avgJitter + 10;

    let rFactor = effectiveLatency < 160
      ? 93.2 - (effectiveLatency / 40)
      : 93.2 - (effectiveLatency / 120) - 10;

    rFactor -= packetsLoss * 2.5;

    // Clamp R-Factor to sane bounds
    rFactor = Math.max(0, Math.min(100, rFactor));

    const mos = 1 + 0.035 * rFactor + 0.000007 * rFactor * (rFactor - 60) * (100 - rFactor);

    return Math.max(1.0, Math.min(5.0, mos));
  }

  start() {
    if (this.#qualityDetector) {
      this.#qualityDetector.setPeerConnection(this.#connection);
    }
    if (this.#issueDetector) {
      this.#issueDetector.setPeerConnection(this.#connection);
      this.#webrtcFlushIssuesInterval = setInterval(() => {
        this.flushWebrtcIssuesQueue();
      }, this.#webrtcIssuesIntervalTime)
    }
  }

  stop() {
    if (this.#webrtcFlushIssuesInterval) {
      clearInterval(this.#webrtcFlushIssuesInterval);
      this.flushWebrtcIssuesQueue();
      this.#webrtcFlushIssuesInterval = null;
    }
    if (this.#qualityDetector) {
      this.#qualityDetector.stop();
    }
    if (this.#issueDetector) {
      this.#issueDetector.stop();
    }
    this.#qualityDetector = null;
    this.#issueDetector = null;
    this.#connection = null;
    this.#eventListeners.clear();
  }

  flushWebrtcIssuesQueue() {
    if (this.#webrtcIssuesQueue.warning.length) {
      this.emit("issues", this.#webrtcIssuesQueue.warning);
    }
    if (this.#webrtcIssuesQueue.stats.length) {
      this.emit("scores-updated", this.#webrtcIssuesQueue.stats);
    }
    this.#webrtcIssuesQueue.warning = []
    this.#webrtcIssuesQueue.stats = []
  }

  onWebRTCIssue(issues) {
    logger.warn('webrtc issues detected', { issues: issues.map(i => `${i.type}/${i.reason}`) });
    this.#webrtcIssuesQueue.warning.push({ timestamp: Date.now(), metrics: issues })
  }

  onWebRTCNetworkScoresUpdated(scores) {
    // https://www.webrtc-developers.com/webrtc-statistics-using-getstats/#display-a-quality-indicator
    /* example payload:
    {
      inbound: 4.403401591252691,
      statsSamples: {
        inboundStatsSample: {
          avgJitter: 0.002,
          packetLoss: 0,
          rtt: 2
        }
      }
    }
    */
    logger.debug('webrtc score sample', { inbound: scores.inbound });
    // only tracking inbound
    if (scores.inbound) {
      this.#webrtcIssuesQueue.stats.push({ timestamp: Date.now(), metrics: scores })
    }
  }

  /**
   * Adds an event callback
   * @param {string} eventType The type of event
   * @param {string} eventCallback A callback
   */
  on(eventType, eventCallback) {
    this.#eventListeners.set(eventType, eventCallback);
  }

  emit(eventType, ...eventArgs) {
    const callback = this.#eventListeners.get(eventType);
    if (callback) {
      // unwrap varargs and call callback
      callback.apply(null, eventArgs);
    }
  }
}

export { WebRTCConnectionWatcher };